/**
 * Delivery Time Utilities
 * Shared utility functions for time calculations and formatting
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST_TIMEZONE = "Asia/Kolkata";

// Default Quick ETA options in minutes
export const DEFAULT_ETA_MINS = [20, 25, 45, 60, 120, 150, 200, 220, 250, 300];

/**
 * Convert minutes to readable format
 * @example minutesToHours(90) => "1:30 hrs"
 * @example minutesToHours(60) => "1 hrs"
 * @example minutesToHours(30) => "30 mins"
 */
export function minutesToHours(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours === 0) return `${remainingMinutes} mins`;
  if (remainingMinutes > 0) return `${hours}:${remainingMinutes} hrs`;
  return `${hours} hrs`;
}

/**
 * Calculate future date for datetime-local input
 * Returns IST datetime string formatted for datetime-local input (YYYY-MM-DDTHH:mm)
 */
export function calculateFutureDate(minutes: number): string {
  const futureIST = dayjs().tz(IST_TIMEZONE).add(minutes, "minutes");
  return futureIST.format("YYYY-MM-DDTHH:mm");
}

// /**
//  * Format CPT for display
//  * @example formatCPTDisplay("2024-01-15T14:30:00Z") => "15 JAN, 2:30 PM"
//  */
// export function formatCPTDisplay(isoString: string): string {
//   const date = new Date(isoString);
//   const day = date.getDate();
//   const month = date.toLocaleDateString("en-IN", { month: "short" });
//   const time = date.toLocaleTimeString("en-IN", {
//     hour: "numeric",
//     minute: "2-digit",
//     hour12: true,
//   });
//   return `${day} ${month}, ${time}`.toUpperCase();
// }

/**
 * Parse quick ETA minutes from config string
 * Falls back to DEFAULT_ETA_MINS if parsing fails
 */
export function parseQuickEtaMins(quickEtasMins?: string): number[] {
  if (!quickEtasMins) return DEFAULT_ETA_MINS;
  const parsed = quickEtasMins
    .split(",")
    .map((m) => parseInt(m.trim()))
    .filter((m) => !isNaN(m));
  return parsed.length > 0 ? parsed : DEFAULT_ETA_MINS;
}

/**
 * Format date/time as "Today, 7:15 PM" or "Tomorrow, 7:15 PM" or "Dec 20, 7:15 PM"
 */
export function formatSmartDateTime(isoString: string): string {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "";

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const inputDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  const timeStr = date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  });

  // Convert am/pm to uppercase AM/PM
  const timeStrUpper = timeStr.replace(/\b(am|pm)\b/gi, (match) =>
    match.toUpperCase()
  );

  if (inputDate.getTime() === today.getTime()) {
    return `Today, ${timeStrUpper}`;
  } else if (inputDate.getTime() === tomorrow.getTime()) {
    return `Tomorrow, ${timeStrUpper}`;
  } else {
    const dateStr = date.toLocaleDateString("en-IN", {
      month: "short",
      day: "numeric",
      timeZone: "Asia/Kolkata",
    });
    return `${dateStr}, ${timeStrUpper}`;
  }
}

/**
 * Calculate days from today to a given date
 */
function daysFromToday(dateString: string): number {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 0;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  const diffMs = targetDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

/**
 * Format CPT range display
 * - If range includes today or tomorrow (0-1 days): shows "Today 7 PM" or "Tomorrow 7 PM" format
 * - If range is 2+ days away: shows "X - Y Days" format
 */
export function formatCPTRange(cptMin: string, cptMax: string): string {
  if (!cptMin || !cptMax) return "";

  const minDays = daysFromToday(cptMin);
  const maxDays = daysFromToday(cptMax);

  // If range includes today or tomorrow, use smart date/time format
  if (minDays <= 1 || maxDays <= 1) {
    if (cptMin === cptMax) {
      return formatSmartDateTime(cptMax);
    }
    return `${formatSmartDateTime(cptMin)} - ${formatSmartDateTime(cptMax)}`;
  }

  // If both dates are 2+ days away, use days format
  if (minDays === maxDays) {
    return `${minDays} Days`;
  }

  return `${minDays} - ${maxDays} Days`;
}
